import React from "react";
import { ArrowRight, Globe2, Anchor, Route, Network } from "lucide-react";
import { Page } from "@/components/Page";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, RevealStagger, StaggerItem } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { WorldMap } from "@/components/WorldMap";
import { IMAGES } from "@/data/site";

const REGIONS = [
  { name: "Asia", text: "Principal manufacturing and transhipment hubs spanning Singapore, Shanghai, Hong Kong, Tokyo and the wider Far East, connecting the East to global markets.", ports: "Singapore \u00b7 Shanghai \u00b7 Hong Kong \u00b7 Tokyo \u00b7 Colombo" },
  { name: "Middle East", text: "Strategic Gulf gateways — Jebel Ali, Sohar, Salalah and Jeddah — anchoring east-west trade and regional distribution.", ports: "Jebel Ali \u00b7 Sohar \u00b7 Salalah \u00b7 Jeddah \u00b7 Dammam" },
  { name: "Europe", text: "Access to Northern European and Mediterranean gateways including Rotterdam, Antwerp, Hamburg and Felixstowe for onward continental distribution.", ports: "Rotterdam \u00b7 Antwerp \u00b7 Hamburg \u00b7 Felixstowe \u00b7 Barcelona" },
  { name: "Africa", text: "Trade lanes serving East, West and Southern Africa through Djibouti, Mombasa, Lagos, Tema and Abidjan with reliable connectivity.", ports: "Djibouti \u00b7 Mombasa \u00b7 Lagos \u00b7 Tema \u00b7 Abidjan" },
  { name: "North America", text: "Connectivity to major North American gateways across both coasts — New York, Los Angeles, Vancouver and Halifax.", ports: "New York \u00b7 Los Angeles \u00b7 Vancouver \u00b7 Halifax" },
  { name: "South America", text: "Established links into key South American markets through S\u00e3o Paulo, Rio de Janeiro and Buenos Aires.", ports: "S\u00e3o Paulo \u00b7 Rio de Janeiro \u00b7 Buenos Aires" },
];

const CAPABILITIES = [
  { icon: Network, title: "International Agent Network", text: "A curated network of trusted partners ensuring local expertise at every port and corridor." },
  { icon: Route, title: "Maritime Route Visualisation", text: "Optimised routings across principal trade lanes balancing cost, transit time and reliability." },
  { icon: Globe2, title: "Worldwide Connectivity", text: "Seamless door-to-door movement across six global regions through coordinated multimodal links." },
  { icon: Anchor, title: "Global Logistics Capabilities", text: "Integrated services that extend from origin to destination under a single point of accountability." },
];

const GlobalNetwork = () => {
  return (
    <Page>
      <SEO title="Global Network" description="Speed Shipping Agencies connects Asia, the Middle East, Europe, Africa and the Americas through an international agent network and animated maritime trade corridors." />

      <PageHero
        eyebrow="Global Network"
        title="International scale, operational reach"
        intro="A coordinated network of agents and maritime partners spanning six global regions — connecting the world's principal trade corridors."
        image={IMAGES.u_containers2}
      />

      {/* Map */}
      <section className="py-24 lg:py-28 bg-ink">
        <div className="ssa-container">
          <div className="max-w-3xl mb-10">
            <SectionHeading dark eyebrow="Maritime Route Visualisation" title="Our trade corridors, mapped" intro="An intelligent route visualisation cycles through each region — illuminating hubs, animating live trade lanes and revealing supporting ports. Hover or select a corridor to explore it." />
          </div>
          <Reveal>
            <WorldMap />
          </Reveal>
        </div>
      </section>

      {/* Regions */}
      <section className="py-24 lg:py-32">
        <div className="ssa-container">
          <SectionHeading align="center" eyebrow="Regional Coverage" title="Connected across key global regions" className="mb-14" />
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {REGIONS.map((r) => (
              <StaggerItem key={r.name}>
                <div className="h-full rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-8 transition-shadow duration-300 hover:shadow-[0_22px_55px_rgba(8,28,58,0.12)]">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl text-navy">{r.name}</h3>
                    <Globe2 className="h-5 w-5 text-turquoise" />
                  </div>
                  <p className="mt-3 text-sm text-secondary-ssa leading-relaxed">{r.text}</p>
                  <div className="mt-5 pt-5 border-t border-[color:var(--ssa-border-subtle)]">
                    <div className="eyebrow text-secondary-ssa mb-1">Key Gateways</div>
                    <div className="font-mono-ssa text-sm text-royal">{r.ports}</div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 lg:py-32 ocean-sheen">
        <div className="ssa-container">
          <SectionHeading align="center" eyebrow="Global Capabilities" title="Built for worldwide connectivity" className="mb-14" />
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAPABILITIES.map((c) => (
              <StaggerItem key={c.title}>
                <div className="h-full rounded-2xl bg-white border border-[color:var(--ssa-border-subtle)] p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-turquoise">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-lg text-navy">{c.title}</h3>
                  <p className="mt-3 text-sm text-secondary-ssa leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
          <Reveal delay={0.1}>
            <div className="mt-14 text-center">
              <Button to="/contact" variant="primary" size="lg">Connect with our network <ArrowRight className="h-4 w-4" /></Button>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
};

export default GlobalNetwork;
